import { Link } from 'react-router-dom';
import { SeoPage, CtaInline } from './SeoLayout';
import { BUSINESS, getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';
import { MapPin, Clock } from 'lucide-react';

// Echte bediente Regionen mit sinnvoll unterschiedlichem Kontext — kein Ortsspam
const REGIONS = [
  {
    name: 'Dormagen',
    zip: '41539 · 41540 · 41541 · 41542',
    zone: 'Hauptsitz',
    text: 'Unser Hauptsitz. Wir sind in allen Dormagener Stadtteilen regelmäßig unterwegs — von Mitte über Horrem und Delrath bis Zons und Rheinfeld. Kurzfristige Termine sind hier meist am ehesten möglich, oft im 24-Stunden-Fenster.',
    districts: ['Zons', 'Nievenheim', 'Delhoven', 'Hackenbroich', 'Stürzelberg', 'Straberg', 'Delrath', 'Gohr', 'Rheinfeld', 'Horrem']
  },
  {
    name: 'Neuss',
    zip: '41460 – 41472',
    zone: 'Standard-Zone',
    text: 'Nachbarstadt Dormagens. Wir arbeiten regelmäßig in Neuss und den umliegenden Stadtteilen. Anfahrt vom Betrieb aus etwa 20 Minuten — keine Fahrtkostenpauschale innerhalb der Standard-Zone.'
  },
  {
    name: 'Köln',
    zip: '50667 – 51149',
    zone: 'Standard-Zone',
    text: 'Nord-Köln und die linksrheinischen Stadtteile bedienen wir standardmäßig. Für Aufträge in südlichen Stadtteilen oder rechtsrheinisch kann ein moderater Anfahrtszuschlag anfallen — wir sagen es transparent im Angebot.'
  },
  {
    name: 'Düsseldorf',
    zip: '40210 – 40629',
    zone: 'Standard-Zone',
    text: 'Klassisches Einsatzgebiet aus dem Dormagener Süden. Innenstadt, Oberkassel, Golzheim, Derendorf, Bilk — alles regelmäßig bearbeitete Postleitzahlen. Terminfenster meist 1–2 Wochen im Voraus.'
  },
  {
    name: 'Leverkusen',
    zip: '51371 – 51381',
    zone: 'Erweiterte Zone',
    text: 'Wir übernehmen Aufträge in Leverkusen, insbesondere in den Stadtteilen mit guter Autobahn-Anbindung. Für kleinere Einzelaufträge kann eine kleine Anfahrtspauschale anfallen — Details klären wir vor dem Termin.'
  },
  {
    name: 'Monheim am Rhein',
    zip: '40789',
    zone: 'Standard-Zone',
    text: 'Direkt zwischen Dormagen und Leverkusen gelegen. Für Baumberg und Zentrum sind wir schnell vor Ort.'
  },
  {
    name: 'Grevenbroich',
    zip: '41515 – 41517',
    zone: 'Erweiterte Zone',
    text: 'Aufträge in Grevenbroich und den Umlandstadtteilen führen wir ab einem gewissen Auftragsumfang gerne durch. Bei kleinen Zimmer-Renovierungen empfehlen wir, mehrere Aufträge zu bündeln, damit die Anfahrt sich rechnet.'
  },
  {
    name: 'Meerbusch',
    zip: '40667 – 40670',
    zone: 'Standard-Zone',
    text: 'Vor allem Büderich und Osterath bearbeiten wir häufig. Anfahrt vergleichbar mit Düsseldorf-West.'
  }
];

export default function EinsatzgebietPage() {
  return (
    <SeoPage
      title="Einsatzgebiet | FarbWelt Maler Express Service Dormagen"
      description="Dormagen und Umgebung – hier arbeiten wir tatsächlich. Neuss, Köln, Düsseldorf, Leverkusen, Monheim, Meerbusch, Grevenbroich. Standard- und erweiterte Zonen transparent erklärt."
      canonicalPath="/einsatzgebiet"
      kicker="Einsatzgebiet"
      heroTitle="Wo wir für Sie arbeiten"
      heroSubtitle="Wir sind keine bundesweite Kette. Wir sind ein Malerbetrieb aus Dormagen und arbeiten dort, wo wir schnell, verlässlich und ohne Fahrerei-Aufpreis sein können."
      jsonLd={[
        getLocalBusinessSchema({
          areaServed: REGIONS.map(r => ({ '@type': 'City', name: r.name }))
        }),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Einsatzgebiet', item: '/einsatzgebiet' }
        ])
      ]}
    >
      <div className="not-prose grid gap-4">
        {REGIONS.map(r => (
          <div key={r.name} className="p-5 sm:p-6 bg-white border border-gray-200 rounded-xl">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
              <div>
                <h2 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2 mt-0">
                  <MapPin className="w-5 h-5 text-[#9B1B30]" />
                  {r.name}
                </h2>
                <div className="text-xs text-gray-500 mt-1">{r.zip}</div>
              </div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                r.zone === 'Hauptsitz' ? 'bg-[#9B1B30] text-white' :
                r.zone === 'Standard-Zone' ? 'bg-green-100 text-green-800' :
                'bg-amber-100 text-amber-800'
              }`}>{r.zone}</span>
            </div>
            <p className="text-gray-700 leading-relaxed mb-3">{r.text}</p>
            {r.districts && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Stadtteile / Ortsteile</div>
                <div className="flex flex-wrap gap-1.5">
                  {r.districts.map(d => (
                    <span key={d} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <CtaInline text="Ihr Ort nicht dabei? Fragen Sie trotzdem an." />

      <h2 className="mt-10">Was heißt Standard- und erweiterte Zone?</h2>
      <p>
        <strong>Standard-Zone:</strong> Regelmäßig bediente Orte — keine Fahrtkosten­pauschale, Anfahrt ist im
        Auftrag enthalten.<br />
        <strong>Erweiterte Zone:</strong> Wir arbeiten dort ebenfalls, kalkulieren aber ab kleinen
        Auftrags­volumen eine kleine, klar ausgewiesene Anfahrts­pauschale. Ab einem gewissen Umfang
        entfällt sie in der Regel — Details klären wir vor Angebots­abgabe.
      </p>

      <div className="not-prose bg-[#faf7f5] border-l-4 border-[#9B1B30] p-5 rounded-r-lg my-8">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-1" />
          <div>
            <div className="font-semibold text-[#1A1A1A] mb-1">Kurzfristig frei?</div>
            <p className="text-gray-700 mb-3">
              Innerhalb Dormagen können wir in vielen Fällen kurzfristig (24–72&nbsp;Stunden) reagieren. Für alle
              anderen Zonen planen wir bevorzugt 1–2 Wochen im Voraus.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={`tel:${BUSINESS.phone}`} className="inline-flex items-center gap-2 text-[#9B1B30] font-semibold">
                {BUSINESS.phoneDisplay}
              </a>
              <Link to="/termin" className="inline-flex items-center gap-2 text-[#9B1B30] font-semibold">
                Termin online buchen →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SeoPage>
  );
}
